// src/ui/FlightCard.jsx — the in-chat flight card.
//
// The phone's card is a white 80%-width box: airline row, two airport codes with a
// plane sliding along a flat grey line, cities, times, done. This is the same
// INFORMATION with a proper desktop treatment — a real progress track with a filled
// leg, a status pill, per-airport local times with dates, and a computed duration —
// because a card that's 320px wide on a phone has a whole laptop to breathe in here.
//
// Two states share one shell:
//   • resolved  — full card
//   • pending   — same chrome, honest empty middle, and a Retry button
// The pending state is not an error. It is what you get when the flight API is out of
// quota, and the message itself has already been delivered regardless.

import React, { useEffect, useRef, useState } from 'react';
import { Plane, Alert } from './icons';
import {
  flightTime, flightDate, flightDuration, flightProgress, crossesDays,
  statusColor, statusLabel, airlineName, flightNumberOf,
  cachedFlight, lookupFlight, refreshFlight, flightApiDown,
} from '../services/flights';

// 🟢 Don't spend quota re-resolving history. A card older than this shows its
//    placeholder and waits for a tap — opening a chat with a dozen old flight
//    messages must not fire a dozen API calls.
const AUTO_RESOLVE_MAX_AGE = 24 * 60 * 60 * 1000;

const REASON_TEXT = {
  breaker: 'Live tracking is rate-limited right now',
  unavailable: 'Live details are unavailable',
  offline: "Couldn't reach the flight service",
  notfound: 'No live details for this flight',
  'cached-miss': 'No live details for this flight',
};

function msOf(ts) {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
  const n = Date.parse(ts);
  return Number.isFinite(n) ? n : 0;
}

/** The two-letter tile — an airline's code IS its logo when you don't ship 300 PNGs. */
function CodeChip({ code, color }) {
  return <span className="fc-chip" style={{ background: color }}>{code || '✈'}</span>;
}

export default function FlightCard({ m, mine }) {
  const canonical = flightNumberOf(m);
  const iata = canonical ? canonical.replace(/\d+$/, '') : '';
  const [data, setData] = useState(m.flightData || null);
  const [reason, setReason] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, force] = useState(0);
  const asked = useRef(false);

  // the message doc can gain flightData later (our own patch, or the phone's sync)
  useEffect(() => { if (m.flightData) { setData(m.flightData); setReason(null); } }, [m.flightData]);

  // resolve from cache synchronously, then from the network only if it's worth it
  useEffect(() => {
    if (data || !canonical) return;
    const hit = cachedFlight(canonical);
    if (hit) { setData(hit); return; }
    if (hit === null) { setReason('cached-miss'); return; }
    if (asked.current) return;
    const age = Date.now() - msOf(m.timestamp);
    if (age > AUTO_RESOLVE_MAX_AGE) { setReason(flightApiDown() ? 'breaker' : null); return; }
    asked.current = true;
    setBusy(true);
    lookupFlight(canonical)
      .then((r) => { if (r.data) setData(r.data); else setReason(r.reason || 'unavailable'); })
      .finally(() => setBusy(false));
  }, [canonical, data, m.timestamp]);

  // 🟢 an airborne flight's progress is a function of NOW, so the plane has to move
  //    on its own — otherwise a card opened an hour ago still shows takeoff.
  useEffect(() => {
    if (data?.status !== 'active') return undefined;
    const t = setInterval(() => force((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [data?.status]);

  const retry = async () => {
    if (!canonical || busy) return;
    setBusy(true); setReason(null);
    const r = await refreshFlight(canonical);
    if (r.data) setData(r.data); else setReason(r.reason || 'unavailable');
    setBusy(false);
  };

  // 🟢 The phone fuses the user's typed sentence onto the card as a coloured header
  //    strip. Here it sits above the card in the same stack — but only when it says
  //    something the card doesn't. "AA123" written above a card that already reads
  //    AA123 is noise, so a message that is *only* the flight number drops it.
  const raw = String(m.text || '').trim();
  const header = raw && raw.replace(/[\s-]/g, '').toUpperCase() !== canonical ? raw : '';

  const name = data?.airline || airlineName(iata) || 'Flight';
  const num = data?.flightNumber || canonical || '';
  const color = statusColor(data?.status);

  return (
    <div className={`fc-wrap ${mine ? 'out' : 'in'}`}>
      {header && <div className="fc-text">{header}</div>}

      <div className={`fc ${data ? '' : 'ghost'}`}>
        <div className="fc-top">
          <CodeChip code={iata} color={data ? color : 'var(--text-3)'} />
          <span className="fc-id">
            <b title={name}>{name}</b>
            <i>{num}</i>
          </span>
          {data ? (
            <span className="fc-pill" style={{ color, borderColor: color }}>
              <span className="fc-dot" style={{ background: color }} />
              {statusLabel(data.status)}
            </span>
          ) : (
            <span className="fc-pill muted">{busy ? 'Checking…' : 'No live data'}</span>
          )}
        </div>

        {data ? <Resolved data={data} color={color} /> : <Pending busy={busy} reason={reason} onRetry={retry} />}
      </div>
    </div>
  );
}

function Resolved({ data, color }) {
  const pct = Math.max(0, Math.min(1, flightProgress(data))) * 100;
  const dur = flightDuration(data);
  const twoDays = crossesDays(data);
  const depDate = flightDate(data.origin?.time);
  const arrDate = flightDate(data.destination?.time);

  return (
    <>
      <div className="fc-route">
        <div className="fc-end">
          <div className="fc-code">{data.origin?.code || '—'}</div>
          <div className="fc-city" title={data.origin?.city}>{data.origin?.city}</div>
        </div>

        <div className="fc-mid">
          <div className="fc-track">
            <span className="fc-fill" style={{ width: `${pct}%`, background: color }} />
            <span className="fc-node a" style={{ borderColor: color }} />
            <span className={`fc-node b ${pct >= 100 ? 'done' : ''}`} style={pct >= 100 ? { borderColor: color, background: color } : undefined} />
            <span className="fc-plane" style={{ left: `${pct}%`, color }}>
              <Plane width={20} height={20} />
            </span>
          </div>
          {dur && <div className="fc-dur">{dur}</div>}
        </div>

        <div className="fc-end right">
          <div className="fc-code">{data.destination?.code || '—'}</div>
          <div className="fc-city" title={data.destination?.city}>{data.destination?.city}</div>
        </div>
      </div>

      <div className="fc-times">
        <div className="fc-t">
          <span className="fc-lab">Departs</span>
          <span className="fc-time">{flightTime(data.origin?.time)}</span>
          {depDate && <span className="fc-date">{depDate}</span>}
        </div>
        <div className="fc-t right">
          <span className="fc-lab">Arrives</span>
          <span className="fc-time" style={data.status === 'landed' ? { color } : undefined}>
            {flightTime(data.destination?.time)}
          </span>
          {arrDate && <span className="fc-date">{twoDays ? `${arrDate} · +1` : arrDate}</span>}
        </div>
      </div>

      {/* 🟢 stated, not assumed: these are each airport's own clock, which is what a
          boarding pass says and what the phone's card gets wrong by converting both
          legs into the reader's timezone. */}
      <div className="fc-foot">Local time at each airport</div>
    </>
  );
}

function Pending({ busy, reason, onRetry }) {
  if (busy) {
    return (
      <div className="fc-skel">
        <span className="fc-sk code" /><span className="fc-sk line" /><span className="fc-sk code" />
      </div>
    );
  }
  return (
    <div className="fc-pending">
      <span className="fc-pend-ico"><Alert width={17} height={17} /></span>
      <span className="fc-pend-txt">{REASON_TEXT[reason] || 'Live details are unavailable'}</span>
      <button className="fc-retry" onClick={onRetry}>Retry</button>
    </div>
  );
}
